<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { useTemplating, buildTemplateContext } from "#bb/composables/useTemplating";
import { resolveResourcePath } from "#bb/lib/pathContract";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  discoverFields,
  getFieldValue,
  formatFieldValue,
  matchesFilter,
  getScorePercent,
  emptySelection,
  isSelectionActive,
  normalizeConfig,
  buildFieldIndex,
  NONE_OPTION_ID,
  type Field,
  type FacetSelection,
  type FacetConfig,
  type ColumnConfig,
  type DisplayType,
} from "./fields";

const logger = useLogger("RankedExplorerRaw");
const props = defineProps<{ id: string }>();
const features = inject<{
  resourceDefinitionPath?: string | null;
}>("render-features", {});

const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));


// --- Commands ---
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this block",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Ranked Explorer (Raw)",
    icon: "lucide:filter",
  },
});

// --- Templating (for individualReportPath) ---
const report = inject<any>("report", null);
const templateContext = computed(() => buildTemplateContext(report));
const templating = computed(() => useTemplating(templateContext, { logger }));
const templatedIndividualPath = computed(() =>
  templating.value.applyTemplate(config.value.general.individualReportPath || "")
);

// --- Loading ---
const { reportData, loading: storeLoading, fetchReports } = useReportsStore();
const isLoading = computed(() =>
  report ? !report.initialized || report.loading : storeLoading.value
);

// --- Raw user data ---
const rawUsers = ref<any[]>([]);

function syncData() {
  if (report) {
    if (!report.initialized || report.loading) {
      rawUsers.value = [];
      return;
    }
    // Prefer the provider's raw users (unmapped) so a custom mapper that reshapes
    // `report.data` (e.g. flattening to {name, score}) can't hide the per-user
    // constructs/qualifiers the explorer ranks + facets on.
    const raw = report.context?.rawUsers;
    rawUsers.value = Array.isArray(raw)
      ? raw
      : Array.isArray(report.data)
        ? report.data
        : [];
    return;
  }
  rawUsers.value = Array.isArray(reportData.value) ? reportData.value : [];
}

onMounted(async () => {
  if (!report) await fetchReports();
  syncData();
});

if (report) {
  watch(() => report.data, syncData, { deep: true });
  watch(() => report.loading, syncData);
  watch(() => report.initialized, syncData);
} else {
  watch(reportData, syncData);
}

// --- Config (normalized from either old or new format) ---
const config = computed(() => normalizeConfig(definitionData.value));

const isConfigured = computed(
  () => config.value.facets.length > 0 || config.value.columns.length > 0
);

// --- Field catalog discovered from data ---
const fieldCatalog = computed<Field[]>(() => discoverFields(rawUsers.value));
const catalogById = computed(() => buildFieldIndex(fieldCatalog.value));

interface ResolvedFacet {
  facet: FacetConfig;
  field: Field | undefined;
}
interface ResolvedColumn {
  col: ColumnConfig;
  field: Field | undefined;
}

const filterFields = computed<ResolvedFacet[]>(() =>
  config.value.facets
    .map((f) => ({ facet: f, field: catalogById.value.get(f.fieldId) }))
    .filter((r) => r.field)
);

const columnFields = computed<ResolvedColumn[]>(() =>
  config.value.columns
    .map((c) => ({ col: c, field: catalogById.value.get(c.fieldId) }))
    .filter((r) => r.field)
);

// --- Facet selections ---
const facetSelections = reactive<Record<string, FacetSelection>>({});

function ensureSelection(fieldId: string, mode: "options" | "text"): FacetSelection {
  if (!facetSelections[fieldId] || facetSelections[fieldId].mode !== mode) {
    facetSelections[fieldId] = emptySelection(mode);
  }
  return facetSelections[fieldId];
}

function toggleOption(fieldId: string, optionId: string) {
  const sel = ensureSelection(fieldId, "options");
  if (optionId === NONE_OPTION_ID) {
    sel.includeNone = !sel.includeNone;
  } else {
    const idx = sel.optionIds.indexOf(optionId);
    if (idx >= 0) sel.optionIds.splice(idx, 1);
    else sel.optionIds.push(optionId);
  }
}

function isOptionSelected(fieldId: string, optionId: string): boolean {
  const sel = facetSelections[fieldId];
  if (!sel) return false;
  if (optionId === NONE_OPTION_ID) return sel.includeNone;
  return sel.optionIds.includes(optionId);
}

function setTextQuery(fieldId: string, q: string) {
  const sel = ensureSelection(fieldId, "text");
  sel.text = q;
}

function toggleTextNone(fieldId: string) {
  const sel = ensureSelection(fieldId, "text");
  sel.includeNone = !sel.includeNone;
}

function clearFacet(fieldId: string) {
  delete facetSelections[fieldId];
}

function clearAllFacets() {
  Object.keys(facetSelections).forEach((k) => delete facetSelections[k]);
}

const hasActiveFilters = computed(() =>
  Object.values(facetSelections).some((s) => s && isSelectionActive(s))
);

// --- Build per-field option lists for the sidebar (sorted by frequency observed) ---
const facetOptions = computed<Record<string, { id: string; name: string }[]>>(() => {
  const out: Record<string, { id: string; name: string }[]> = {};
  for (const { field } of filterFields.value) {
    if (!field) continue;
    if (field.kind !== "options") continue;
    out[field.id] = (field.options || [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return out;
});

// --- Filtering ---
const filteredUsers = computed(() => {
  const users = rawUsers.value;
  if (!hasActiveFilters.value) return users;
  return users.filter((user) => {
    for (const { facet, field } of filterFields.value) {
      if (!field) continue;
      const sel = facetSelections[facet.fieldId];
      if (!sel || !isSelectionActive(sel)) continue;
      if (!matchesFilter(user, field, sel)) return false;
    }
    return true;
  });
});

// --- Sorting ---
const sortFieldId = ref<string>(config.value.general.sortFieldId);
const sortOrder = ref<"asc" | "desc">(config.value.general.sortOrder);

watch(
  () => config.value.general.sortFieldId,
  (v) => {
    if (v !== undefined) sortFieldId.value = v;
  }
);
watch(
  () => config.value.general.sortOrder,
  (v) => {
    if (v !== undefined) sortOrder.value = v;
  }
);

function toggleSort(fieldId: string) {
  if (sortFieldId.value === fieldId) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortFieldId.value = fieldId;
    sortOrder.value = "desc";
  }
}

function compareValues(a: unknown, b: unknown): number {
  const aMissing = a === undefined || a === null || a === "";
  const bMissing = b === undefined || b === null || b === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1; // missing always sorts last
  if (bMissing) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (Array.isArray(a)) a = a.join(", ");
  if (Array.isArray(b)) b = b.join(", ");
  return String(a).localeCompare(String(b));
}

const sortedUsers = computed(() => {
  const users = [...filteredUsers.value];
  if (!sortFieldId.value) return users;
  const sortField = catalogById.value.get(sortFieldId.value);
  if (!sortField) return users;
  return users.sort((a, b) => {
    const av = getFieldValue(a, sortField);
    const bv = getFieldValue(b, sortField);
    const cmp = compareValues(av, bv);
    return sortOrder.value === "asc" ? cmp : -cmp;
  });
});

// --- Pagination ---
const pageSize = computed<number>(() => Number(config.value.general.pageSize) || 25);
const currentPage = ref(1);
const totalCount = computed(() => sortedUsers.value.length);
const pageCount = computed(() => Math.ceil(totalCount.value / pageSize.value) || 1);

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return sortedUsers.value.slice(start, start + pageSize.value);
});

watch(filteredUsers, () => {
  currentPage.value = 1;
});

// --- View mode ---
type ViewMode = "list" | "table";
const viewMode = ref<ViewMode>("table");

// --- Helpers ---
function scoreColor(percent: number): string {
  if (percent >= 70) return "bg-green-500";
  if (percent >= 40) return "bg-yellow-500";
  return "bg-red-500";
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renderColumnText(user: any, field: Field): string {
  if (field.kind === "date") {
    const v = getFieldValue(user, field);
    if (v === undefined) return "—";
    return formatDate(String(v));
  }
  if (field.kind === "number") {
    const v = getFieldValue(user, field);
    if (v === undefined) return "—";
    return String(v);
  }
  return formatFieldValue(user, field) || "—";
}

/** Best-effort stable id for a report row; undefined when absent/blank. */
function userIdOf(user: any): string | undefined {
  const raw = user?.userId ?? user?.id ?? user?.UserId;
  if (raw === undefined || raw === null) return undefined;
  const s = String(raw).trim();
  return s && s !== "undefined" && s !== "null" ? s : undefined;
}

function navigateToUser(user: any) {
  const path = templatedIndividualPath.value;
  if (!path) return;

  // Guard against `?user=undefined`, which otherwise cascades into a broken
  // individual report (fetches all users, resolves no candidate, renders nothing).
  const userId = userIdOf(user);
  if (!userId) {
    logger.warn("Ranked explorer: row has no user id; not navigating", user);
    return;
  }

  // Resolve through the surface-aware app router so admin opens the report
  // renderer (?app=reports&fn=render) rather than the file manager; the
  // candidate surface ignores app/fn and keeps its bare `/<path>` URL.
  const { resolve } = useAppRoute();
  const target = resolve(
    resolveResourcePath(path, features.resourceDefinitionPath),
    { app: "reports", fn: "render" },
  ) as {
    path: string;
    query?: Record<string, string>;
  };
  navigateTo({ path: target.path, query: { ...(target.query ?? {}), user: userId } });
}

// --- Refresh (authoritative re-fetch) ---
const reportRefresh = inject<null | (() => Promise<unknown>)>("reportRefresh", null);
const refreshing = ref(false);
async function refreshData() {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    if (reportRefresh) {
      // Provider-backed: re-fetch the full report file with x-refresh and remap.
      await reportRefresh();
    } else {
      // Standalone: hit the store directly with an authoritative refresh.
      await fetchReports(undefined, { refresh: true });
      syncData();
    }
  } catch (e) {
    logger.error("Failed to refresh ranked explorer data", e);
  } finally {
    refreshing.value = false;
  }
}

function selectionCount(sel: FacetSelection | undefined): number {
  if (!sel) return 0;
  let c = 0;
  if (sel.includeNone) c++;
  if (sel.mode === "options") c += sel.optionIds.length;
  else if (sel.text.trim()) c++;
  return c;
}
</script>

<template>
  <div :class="cn($attrs.class ?? '', 'flex gap-4 w-full min-h-[400px]')">
    <!-- Configure-first guard -->
    <template v-if="!isConfigured && !isLoading">
      <div class="flex-1 flex items-center justify-center py-12">
        <div class="text-center space-y-2">
          <icon mode="svg" size="32" name="lucide:settings-2" class="mx-auto text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            Configure filters and columns in the editor to get started.
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <!-- Facet sidebar -->
      <aside
        v-if="filterFields.length"
        class="w-64 shrink-0 overflow-y-auto max-h-[80vh] border-r pr-4"
      >
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold">Filters</h3>
          <button
            v-if="hasActiveFilters"
            class="text-xs text-muted-foreground hover:text-foreground"
            @click="clearAllFacets"
          >
            Clear all
          </button>
        </div>

        <div v-if="isLoading" class="space-y-3">
          <Skeleton v-for="n in 3" :key="n" class="h-20 w-full" />
        </div>

        <Accordion
          v-else
          type="multiple"
          class="w-full"
          :default-value="filterFields.map((r: ResolvedFacet) => r.facet.fieldId)"
        >
          <AccordionItem
            v-for="{ facet, field } in filterFields"
            :key="facet.fieldId"
            :value="facet.fieldId"
          >
            <AccordionTrigger class="py-2 text-xs font-medium uppercase tracking-wide">
              <div class="flex items-center gap-2">
                <span class="truncate">{{ facet.label }}</span>
                <Badge
                  v-if="selectionCount(facetSelections[facet.fieldId])"
                  variant="secondary"
                  class="text-[10px] h-4 px-1"
                >
                  {{ selectionCount(facetSelections[facet.fieldId]) }}
                </Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div class="space-y-1 pb-2">
                <div class="flex justify-end mb-1">
                  <button
                    v-if="selectionCount(facetSelections[facet.fieldId])"
                    class="text-[10px] text-muted-foreground hover:text-foreground"
                    @click="clearFacet(facet.fieldId)"
                  >
                    Clear
                  </button>
                </div>

                <!-- Options mode -->
                <template v-if="facet.mode === 'options' && field">
                  <label
                    v-for="option in facetOptions[facet.fieldId] || []"
                    :key="option.id"
                    class="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      class="rounded border-border"
                      :checked="isOptionSelected(facet.fieldId, option.id)"
                      @change="toggleOption(facet.fieldId, option.id)"
                    />
                    <span class="truncate">{{ option.name }}</span>
                  </label>
                  <label
                    class="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 italic text-muted-foreground border-t mt-1 pt-1"
                  >
                    <input
                      type="checkbox"
                      class="rounded border-border"
                      :checked="isOptionSelected(facet.fieldId, NONE_OPTION_ID)"
                      @change="toggleOption(facet.fieldId, NONE_OPTION_ID)"
                    />
                    <span class="truncate">Not answered</span>
                  </label>
                </template>

                <!-- Text mode -->
                <template v-else-if="facet.mode === 'text'">
                  <input
                    type="search"
                    class="w-full text-xs border rounded px-2 py-1 bg-background"
                    placeholder="Search…"
                    :value="facetSelections[facet.fieldId]?.text ?? ''"
                    @input="
                      setTextQuery(
                        facet.fieldId,
                        ($event.target as HTMLInputElement).value
                      )
                    "
                  />
                  <label
                    class="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 italic text-muted-foreground mt-1"
                  >
                    <input
                      type="checkbox"
                      class="rounded border-border"
                      :checked="facetSelections[facet.fieldId]?.includeNone ?? false"
                      @change="toggleTextNone(facet.fieldId)"
                    />
                    <span class="truncate">Include unanswered</span>
                  </label>
                </template>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </aside>

      <!-- Main content area -->
      <div class="flex-1 min-w-0 space-y-4">
        <!-- Toolbar -->
        <div class="flex items-center justify-between">
          <div class="text-sm text-muted-foreground">
            {{ totalCount }} result{{ totalCount !== 1 ? "s" : "" }}
            <span v-if="hasActiveFilters"> (filtered)</span>
          </div>
          <div class="flex items-center gap-1 p-0.5">
            <button
              type="button"
              aria-label="Refresh data"
              title="Refresh data"
              :disabled="refreshing"
              class="inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground disabled:opacity-60 cursor-pointer"
              @click="refreshData"
            >
              <icon mode="svg" size="16" name="lucide:refresh-cw" :class="refreshing && 'animate-spin'" />
            </button>
            <button
              type="button"
              aria-label="List view"
              :aria-pressed="viewMode === 'list'"
              class="inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-sm transition-colors cursor-pointer"
              :class="
                viewMode === 'list'
                  ? 'bg-muted text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              "
              @click="viewMode = 'list'"
            >
              <icon mode="svg" size="16" name="lucide:list" />
            </button>
            <button
              type="button"
              aria-label="Table view"
              :aria-pressed="viewMode === 'table'"
              class="inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-sm transition-colors cursor-pointer"
              :class="
                viewMode === 'table'
                  ? 'bg-muted text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              "
              @click="viewMode = 'table'"
            >
              <icon mode="svg" size="16" name="lucide:table" />
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="space-y-2">
          <Skeleton v-for="n in 5" :key="n" class="h-12 w-full" />
        </div>

        <!-- Table view -->
        <div v-else-if="viewMode === 'table'" class="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead class="w-11">#</TableHead>
                <TableHead
                  v-for="{ col, field } in columnFields"
                  :key="col.fieldId"
                  class="cursor-pointer select-none hover:bg-muted/50"
                  :class="col.align === 'right' ? 'text-right' : ''"
                  @click="toggleSort(col.fieldId)"
                >
                  <div
                    class="flex items-center gap-1"
                    :class="col.align === 'right' ? 'justify-end' : ''"
                  >
                    {{ col.label }}
                    <Icon mode="svg" size="12"
                      v-if="sortFieldId === col.fieldId"
                      :name="
                        sortOrder === 'asc' ? 'lucide:arrow-up' : 'lucide:arrow-down'
                      "
                    />
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow
                v-for="(user, index) in paginatedUsers"
                :key="user?.userId ?? index"
                :class="userIdOf(user) ? 'cursor-pointer hover:bg-muted/50' : 'cursor-default'"
                @click="navigateToUser(user)"
              >
                <TableCell class="text-muted-foreground">
                  {{ (currentPage - 1) * Number(pageSize) + Number(index) + 1 }}
                </TableCell>
                <TableCell
                  v-for="{ col, field } in columnFields"
                  :key="col.fieldId"
                  :class="col.align === 'right' ? 'text-right' : ''"
                >
                  <template v-if="!field">—</template>
                  <!-- Display type: text — always show raw value -->
                  <template v-else-if="col.display === 'text'">
                    {{ renderColumnText(user, field) }}
                  </template>
                  <!-- Display type: percentage — score as % text only -->
                  <template v-else-if="col.display === 'percentage'">
                    <span class="font-semibold tabular-nums">
                      {{ Math.round(getScorePercent(getFieldValue(user, field))) }}%
                    </span>
                  </template>
                  <!-- Display type: progress — bar + % -->
                  <template v-else-if="col.display === 'progress'">
                    <div class="flex items-center gap-2 justify-end">
                      <span class="text-xs text-muted-foreground tabular-nums">
                        {{ Math.round(getScorePercent(getFieldValue(user, field))) }}%
                      </span>
                      <div class="w-16 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all"
                          :class="scoreColor(getScorePercent(getFieldValue(user, field)))"
                          :style="{
                            width: `${Math.round(getScorePercent(getFieldValue(user, field)))}%`,
                          }"
                        />
                      </div>
                    </div>
                  </template>
                  <!-- Display type: bar — bar only -->
                  <template v-else-if="col.display === 'bar'">
                    <div class="flex justify-end">
                      <div class="w-20 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all"
                          :class="scoreColor(getScorePercent(getFieldValue(user, field)))"
                          :style="{
                            width: `${Math.round(getScorePercent(getFieldValue(user, field)))}%`,
                          }"
                        />
                      </div>
                    </div>
                  </template>
                  <!-- Fallback: text display -->
                  <template v-else>
                    {{ renderColumnText(user, field) }}
                  </template>
                </TableCell>
              </TableRow>
              <TableRow v-if="!paginatedUsers.length">
                <TableCell
                  :colspan="columnFields.length + 1"
                  class="text-center py-8 text-muted-foreground"
                >
                  No results match your filters.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <!-- List view -->
        <div v-else-if="viewMode === 'list'">
          <ul role="list" class="divide-y divide-border">
            <li
              v-for="(user, index) in paginatedUsers"
              :key="user?.userId ?? index"
              class="relative flex justify-between gap-x-6 py-3 hover:bg-muted/50 transition-colors"
              :class="userIdOf(user) ? 'cursor-pointer' : 'cursor-default'"
              @click="navigateToUser(user)"
            >
              <div class="flex min-w-0 gap-x-4">
                <div
                  class="flex size-10 flex-none items-center justify-center rounded-full bg-muted"
                >
                  <span class="text-xs font-semibold text-muted-foreground">
                    {{ (currentPage - 1) * Number(pageSize) + Number(index) + 1 }}
                  </span>
                </div>
                <div class="min-w-0 flex-auto">
                  <p class="text-sm font-semibold text-foreground">
                    {{ user.name || user.userId }}
                  </p>
                  <p class="text-xs text-muted-foreground truncate">
                    {{ user.emailAddress }}
                  </p>
                </div>
              </div>
              <div class="flex shrink-0 items-center gap-x-4">
                <div class="hidden @min-[640px]:flex @min-[640px]:items-center @min-[640px]:gap-3">
                  <template
                    v-for="{ col, field } in columnFields"
                    :key="col.fieldId"
                  >
                    <div v-if="field && field.kind === 'number' && col.display !== 'text'" class="text-right">
                      <p class="text-xs text-muted-foreground">{{ col.label }}</p>
                      <p class="text-sm font-semibold tabular-nums">
                        {{ Math.round(getScorePercent(getFieldValue(user, field))) }}%
                      </p>
                    </div>
                  </template>
                </div>
                <Icon mode="svg" size="20"
                  name="lucide:chevron-right"
                  class="flex-none text-muted-foreground"
                />
              </div>
            </li>
          </ul>
          <div v-if="!paginatedUsers.length" class="py-12 text-center">
            <p class="text-sm text-muted-foreground">
              No results match your filters.
            </p>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="pageCount > 1"
          class="flex items-center justify-between border-t border-border py-3"
        >
          <p class="text-sm text-muted-foreground">
            Showing {{ (currentPage - 1) * Number(pageSize) + 1 }} to
            {{ Math.min(currentPage * Number(pageSize), totalCount) }} of
            {{ totalCount }} results
          </p>
          <div class="flex items-center gap-2">
            <span class="text-sm text-muted-foreground">
              Page {{ currentPage }} of {{ pageCount }}
            </span>
            <Button
              variant="outline"
              size="sm"
              :disabled="currentPage <= 1"
              @click="currentPage--"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              :disabled="currentPage >= pageCount"
              @click="currentPage++"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  editor: () => import("#bb/components/editor/reports/ranked-explorer/index.vue"),
  componentId: ComponentDefinitionIds.RANKED_EXPLORER_RAW,
  icon: "lucide:filter",
  type: "Ranked Explorer (Raw)",
  description:
    "Filterable ranked report. Pick any field — answers, constructs, profile, or timeline — as a filter or column.",
  default: {
    facets: [],
    columns: [],
    general: {
      individualReportPath: "",
      pageSize: 25,
      sortFieldId: "",
      sortOrder: "desc",
    },
  },
  categories: ["Reports"],
  order: 12007,
};
</script>
